/** Interactive SVG charts with hover crosshair + tooltip. Pure SVG + React. */

import { useMemo, useRef, useState } from "react";

function dayLabel(i: number, n: number) {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1 - i));
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ---- Interactive area chart (portfolio / NAV) ---- */
export function LiveAreaChart({
  data,
  color = "var(--accent)",
  height = 230,
  valueFmt = (v: number) => String(v),
  unit = "",
}: {
  data: number[];
  color?: string;
  height?: number;
  valueFmt?: (v: number) => string;
  unit?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const W = 1000;
  const H = height;
  const pad = { t: 16, b: 26, l: 0, r: 0 };
  const min = useMemo(() => Math.min(...data), [data]);
  const max = useMemo(() => Math.max(...data), [data]);
  const span = max - min || 1;
  const X = (i: number) => pad.l + (i / (data.length - 1)) * (W - pad.l - pad.r);
  const Y = (v: number) => pad.t + (1 - (v - min) / span) * (H - pad.t - pad.b);
  const line = data.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(" ");
  const area = `${line} L${X(data.length - 1)} ${H - pad.b} L${X(0)} ${H - pad.b} Z`;
  const gid = useMemo(() => "la" + Math.random().toString(36).slice(2, 7), []);

  const onMove = (e: React.MouseEvent) => {
    const r = wrapRef.current!.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    setHover(Math.max(0, Math.min(data.length - 1, Math.round(px * (data.length - 1)))));
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity="0.26" />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g, i) => (
          <line key={i} x1="0" x2={W} y1={pad.t + g * (H - pad.t - pad.b)} y2={pad.t + g * (H - pad.t - pad.b)} stroke="var(--border)" strokeWidth="1" strokeDasharray="2 6" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={area} fill={`url(#${gid})`} />
        <path className="draw-line" style={{ "--len": 2400 } as React.CSSProperties} d={line} fill="none" stroke={color} strokeWidth="2.4" strokeDasharray="2400" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {hover != null && (
          <g>
            <line x1={X(hover)} x2={X(hover)} y1={pad.t} y2={H - pad.b} stroke={color} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" vectorEffect="non-scaling-stroke" />
            <circle cx={X(hover)} cy={Y(data[hover])} r="5" fill="var(--surface)" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
          </g>
        )}
      </svg>
      {hover != null && (
        <div
          style={{
            position: "absolute",
            left: `${(hover / (data.length - 1)) * 100}%`,
            top: -6,
            transform: `translateX(${hover < data.length * 0.2 ? "0" : hover > data.length * 0.8 ? "-100%" : "-50%"})`,
            pointerEvents: "none",
            background: "var(--text)",
            color: "#fff",
            padding: "7px 11px",
            borderRadius: 9,
            boxShadow: "var(--pop-shadow)",
            whiteSpace: "nowrap",
            zIndex: 5,
          }}
        >
          <div className="mono" style={{ fontSize: 9.5, opacity: 0.65, letterSpacing: "0.06em" }}>{dayLabel(hover, data.length)}</div>
          <div className="mono tnum" style={{ fontSize: 14, fontWeight: 600 }}>{valueFmt(data[hover])}{unit}</div>
        </div>
      )}
    </div>
  );
}

/* ---- Interactive NAV vs Price spread chart (the signature) ---- */
function Tip({ dotColor, k, v }: { dotColor: string; k: string; v: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, justifyContent: "space-between" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: dotColor }} />
        <span className="mono" style={{ fontSize: 10.5, opacity: 0.8 }}>{k}</span>
      </span>
      <span className="mono tnum" style={{ fontSize: 12.5, fontWeight: 600 }}>{v}</span>
    </div>
  );
}

export function LiveSpreadChart({ nav, price, height = 280 }: { nav: number[]; price: number[]; height?: number }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const W = 1000;
  const H = height;
  const pad = { t: 18, b: 28 };
  const all = [...nav, ...price];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const X = (i: number) => (i / (nav.length - 1)) * W;
  const Y = (v: number) => pad.t + (1 - (v - min) / span) * (H - pad.t - pad.b);
  const navLine = nav.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(" ");
  const prLine = price.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(" ");
  const band = `${prLine} ${nav.map((_v, i) => `L${X(nav.length - 1 - i).toFixed(1)} ${Y(nav[nav.length - 1 - i]).toFixed(1)}`).join(" ")} Z`;

  const onMove = (e: React.MouseEvent) => {
    const r = wrapRef.current!.getBoundingClientRect();
    setHover(Math.max(0, Math.min(nav.length - 1, Math.round(((e.clientX - r.left) / r.width) * (nav.length - 1)))));
  };
  const spreadAt = hover != null ? ((price[hover] - nav[hover]) / nav[hover]) * 100 : null;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
        <defs>
          <linearGradient id="lsband" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.16" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g, i) => (
          <line key={i} x1="0" x2={W} y1={pad.t + g * (H - pad.t - pad.b)} y2={pad.t + g * (H - pad.t - pad.b)} stroke="var(--border)" strokeWidth="1" strokeDasharray="2 6" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={band} fill="url(#lsband)" />
        <path d={navLine} fill="none" stroke="var(--micro)" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path className="draw-line" style={{ "--len": 2600 } as React.CSSProperties} d={prLine} fill="none" stroke="var(--accent)" strokeWidth="2.6" strokeDasharray="2600" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {hover != null && (
          <g>
            <line x1={X(hover)} x2={X(hover)} y1={pad.t} y2={H - pad.b} stroke="var(--text)" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" vectorEffect="non-scaling-stroke" />
            <circle cx={X(hover)} cy={Y(nav[hover])} r="4.5" fill="var(--surface)" stroke="var(--micro)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
            <circle cx={X(hover)} cy={Y(price[hover])} r="5" fill="var(--surface)" stroke="var(--accent)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
          </g>
        )}
      </svg>
      {hover != null && spreadAt != null && (
        <div
          style={{
            position: "absolute",
            left: `${(hover / (nav.length - 1)) * 100}%`,
            top: -8,
            transform: `translateX(${hover < nav.length * 0.18 ? "0" : hover > nav.length * 0.82 ? "-100%" : "-50%"})`,
            pointerEvents: "none",
            background: "var(--text)",
            color: "#fff",
            padding: "9px 13px",
            borderRadius: 10,
            boxShadow: "var(--pop-shadow)",
            whiteSpace: "nowrap",
            zIndex: 5,
            minWidth: 132,
          }}
        >
          <div className="mono" style={{ fontSize: 9.5, opacity: 0.6, letterSpacing: "0.06em", marginBottom: 4 }}>{dayLabel(hover, nav.length)}</div>
          <Tip dotColor="var(--accent-bright)" k="Price" v={price[hover].toFixed(4)} />
          <Tip dotColor="rgba(255,255,255,0.6)" k="NAV" v={nav[hover].toFixed(4)} />
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", marginTop: 5, paddingTop: 5, display: "flex", justifyContent: "space-between", gap: 14 }}>
            <span className="mono" style={{ fontSize: 10.5, opacity: 0.7 }}>spread</span>
            <span className="mono tnum" style={{ fontSize: 12.5, fontWeight: 700, color: spreadAt >= 0 ? "#5EE6C8" : "#FF9DB0" }}>
              {spreadAt >= 0 ? "+" : ""}{spreadAt.toFixed(2)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
